import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Chip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import UsersService, {
  User,
  UserCreate,
  UserUpdate,
  UserFilter,
  Role
} from '../../services/usersService';
import { API_BASE_URL } from '../../config';

// Service for getting clients
const ClientesService = {
  baseUrl: API_BASE_URL.replace('/api/v1', ''),
  
  getAllClientes: async (): Promise<Cliente[]> => {
    try {
      const url = `${ClientesService.baseUrl}/api/v1/clientes/all/list`;
      console.log(`Fetching all clients from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response: Status ${response.status}`, errorText);
        throw new Error(`Erro ao buscar lista de clientes: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching all clients:', error);
      // Return empty array to prevent UI crashes
      return [];
    }
  }
};

// Interface para entidades de referência
interface Cliente {
  id: number;
  nome: string;
}

const UsersPage: React.FC = () => {
  const theme = useTheme();
  
  // Estado de listagem
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<UserFilter>({});
  
  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRolesDialog, setOpenRolesDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [newRole, setNewRole] = useState('');
  const [newUser, setNewUser] = useState<UserCreate>({
    email: '',
    login: '',
    nome: '',
    password: ''
  });
  const [editUser, setEditUser] = useState<UserUpdate>({});

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      
      // Fetch real clients data
      const clientesData = await ClientesService.getAllClientes();
      setClientes(clientesData);
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
      
      // Fallback to empty clients list
      setClientes([]);
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const data = await UsersService.getUsers(filter, skip, limit);
      setUsers(data);
      
      // Obter contagem total para paginação
      const count = await UsersService.getUsersCount(filter);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar usuários',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar usuários ao iniciar a página ou mudar filtros/paginação
    fetchUsers();
    // Carregar referências
    carregarReferencias();
  }, [fetchUsers, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof UserFilter, value: string | number | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Função para limpar filtro de busca
  const clearSearchFilter = (field: keyof UserFilter) => {
    setFilter((prev) => ({
      ...prev,
      [field]: undefined
    }));
    setPage(0);
  };

  const handleCreateUser = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newUser.email || !newUser.nome || !newUser.login || !newUser.password) {
        setSnackbar({
          open: true,
          message: 'Todos os campos obrigatórios devem ser preenchidos',
          severity: 'error',
        });
        return;
      }

      const result = await UsersService.createUser(newUser);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Usuário criado com sucesso',
        severity: 'success',
      });
      
      // Limpar formulário
      setNewUser({
        email: '',
        login: '',
        nome: '',
        password: ''
      });
      
      // Recarregar dados
      fetchUsers();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar usuário',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (user: User) => {
    setCurrentUser(user);
    // Inicializar dados para edição (excluindo senha que não é exibida)
    setEditUser({
      email: user.email,
      login: user.login,
      nome: user.nome,
      cliente_id: user.cliente_id
    });
    
    setOpenEditDialog(true);
  };

  const handleEditUser = async () => {
    if (!currentUser) return;

    try {
      // Verificar campos obrigatórios
      if (!editUser.email || !editUser.nome || !editUser.login) {
        setSnackbar({
          open: true,
          message: 'Email, nome e login são obrigatórios',
          severity: 'error',
        });
        return;
      }

      await UsersService.updateUser(currentUser.id, editUser);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Usuário atualizado com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentUser(null);
      setEditUser({});
      
      // Recarregar dados
      fetchUsers();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar usuário',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (user: User) => {
    setCurrentUser(user);
    setOpenDeleteDialog(true);
  };

  const handleDeleteUser = async () => {
    if (!currentUser) return;

    try {
      await UsersService.deleteUser(currentUser.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Usuário excluído com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentUser(null);
      
      // Recarregar dados
      fetchUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir usuário',
        severity: 'error',
      });
    }
  };

  const handleOpenRolesDialog = async (user: User) => {
    setCurrentUser(user);
    setOpenRolesDialog(true);
    
    try {
      const roles = await UsersService.getUserRoles(user.id);
      setUserRoles(roles);
    } catch (error) {
      console.error('Erro ao buscar roles do usuário:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar roles do usuário',
        severity: 'error',
      });
    }
  };
  
  const handleAddRole = async () => {
    if (!currentUser || !newRole) return;
    
    try {
      await UsersService.addUserRole(currentUser.id, newRole);
      setSnackbar({
        open: true,
        message: `Role ${newRole} adicionada com sucesso`,
        severity: 'success',
      });
      
      // Atualizar lista de roles
      const roles = await UsersService.getUserRoles(currentUser.id);
      setUserRoles(roles);
      setNewRole('');
    } catch (error) {
      console.error('Erro ao adicionar role:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao adicionar role',
        severity: 'error',
      });
    }
  };
  
  const handleRemoveRole = async (role: string) => {
    if (!currentUser) return;
    
    try {
      await UsersService.removeUserRole(currentUser.id, role);
      setSnackbar({
        open: true,
        message: `Role ${role} removida com sucesso`,
        severity: 'success',
      });
      
      // Atualizar lista de roles
      const roles = await UsersService.getUserRoles(currentUser.id);
      setUserRoles(roles);
    } catch (error) {
      console.error('Erro ao remover role:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao remover role',
        severity: 'error',
      });
    }
  };

  // Handler para alterações em selects
  const handleSelectChange = (event: SelectChangeEvent<number | string | undefined>, setter: Function) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setter((prev: any) => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <SidebarLayout>
      <Box 
        sx={{
          p: { xs: 2, md: 4 },
          backgroundImage: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        <Typography 
          variant="h4" 
          gutterBottom 
          display="flex" 
          alignItems="center"
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main,
            mb: 3,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 60,
              height: 3,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 1
            }
          }}
        >
          <PersonIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
          Gerenciamento de Usuários
        </Typography>
        
        {/* Área de filtros */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 4, 
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Buscar por email"
                variant="outlined"
                size="small"
                value={filter.email || ''}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.light }} />
                    </InputAdornment>
                  ),
                  endAdornment: filter.email ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => clearSearchFilter('email')}
                        edge="end"
                        sx={{ color: theme.palette.grey[500] }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Buscar por nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.light }} />
                    </InputAdornment>
                  ),
                  endAdornment: filter.nome ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => clearSearchFilter('nome')}
                        edge="end"
                        sx={{ color: theme.palette.grey[500] }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" sx={{ 
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.light
                  }
                }
              }}>
                <InputLabel id="cliente-filter-label">Cliente</InputLabel>
                <Select
                  labelId="cliente-filter-label"
                  label="Cliente"
                  name="cliente_id"
                  value={filter.cliente_id || ''}
                  onChange={(e) => handleFilterChange('cliente_id', e.target.value === '' ? undefined : Number(e.target.value))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{ 
                  height: '100%',
                  backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                  '&:hover': {
                    boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
                }}
              >
                Novo Usuário
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Usuários */}
        <Paper 
          elevation={0} 
          sx={{ 
            width: '100%', 
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: '0 3px 10px rgba(0,0,0,0.08)'
          }}
        >
          <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: theme.palette.background.paper, 
                    color: theme.palette.primary.main 
                  }}>ID</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: theme.palette.background.paper, 
                    color: theme.palette.primary.main 
                  }}>Nome</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: theme.palette.background.paper, 
                    color: theme.palette.primary.main 
                  }}>Email</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: theme.palette.background.paper, 
                    color: theme.palette.primary.main 
                  }}>Login</TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: theme.palette.background.paper, 
                    color: theme.palette.primary.main 
                  }}>Cliente</TableCell>
                  <TableCell align="right" sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: theme.palette.background.paper, 
                    color: theme.palette.primary.main 
                  }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <PersonIcon sx={{ fontSize: 48, color: theme.palette.grey[400] }} />
                        <Typography variant="body1" color="textSecondary">
                          Nenhum usuário encontrado
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow 
                      key={user.id} 
                      hover
                      sx={{
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: `${theme.palette.primary.main}08`
                        }
                      }}
                    >
                      <TableCell>{user.id}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{user.nome}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.login}</TableCell>
                      <TableCell>
                        {user.cliente_id ? (
                          <Chip 
                            size="small" 
                            label={clientes.find(c => c.id === user.cliente_id)?.nome || '-'}
                            sx={{ 
                              backgroundColor: `${theme.palette.info.main}15`,
                              color: theme.palette.info.dark,
                              fontWeight: 500
                            }}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenRolesDialog(user)}
                            title="Gerenciar Permissões"
                            sx={{ 
                              backgroundColor: `${theme.palette.primary.main}10`,
                              '&:hover': { 
                                backgroundColor: `${theme.palette.primary.main}20`
                              },
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <VpnKeyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(user)}
                            title="Editar"
                            sx={{ 
                              backgroundColor: `${theme.palette.primary.main}10`,
                              '&:hover': { 
                                backgroundColor: `${theme.palette.primary.main}20`
                              },
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(user)}
                            title="Excluir"
                            sx={{ 
                              backgroundColor: `${theme.palette.error.main}10`,
                              '&:hover': { 
                                backgroundColor: `${theme.palette.error.main}20`
                              },
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            borderTop: `1px solid ${theme.palette.grey[200]}`
          }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </Box>
        </Paper>
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AddIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              Novo Usuário
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newUser.nome || ''}
                onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                required
                value={newUser.email || ''}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Login"
                name="login"
                required
                value={newUser.login || ''}
                onChange={(e) => setNewUser({ ...newUser, login: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Senha"
                name="password"
                type="password"
                required
                value={newUser.password || ''}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2
                  }
                }
              }}>
                <InputLabel id="new-cliente-label">Cliente</InputLabel>
                <Select
                  labelId="new-cliente-label"
                  label="Cliente"
                  name="cliente_id"
                  value={newUser.cliente_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewUser)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.grey[200]}` }}>
          <Button 
            onClick={() => setOpenCreateDialog(false)}
            sx={{ color: theme.palette.grey[700] }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={!newUser.nome || !newUser.email || !newUser.login || !newUser.password}
            sx={{ 
              px: 3,
              backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
              '&:hover': {
                boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`
              }
            }}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EditIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              Editar Usuário
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={editUser.nome || ''}
                onChange={(e) => setEditUser({ ...editUser, nome: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                required
                value={editUser.email || ''}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Login"
                name="login"
                required
                value={editUser.login || ''}
                onChange={(e) => setEditUser({ ...editUser, login: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nova Senha (deixe em branco para manter a atual)"
                name="password"
                type="password"
                value={editUser.password || ''}
                onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2
                  }
                }
              }}>
                <InputLabel id="edit-cliente-label">Cliente</InputLabel>
                <Select
                  labelId="edit-cliente-label"
                  label="Cliente"
                  name="cliente_id"
                  value={editUser.cliente_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditUser)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.grey[200]}` }}>
          <Button 
            onClick={() => setOpenEditDialog(false)}
            sx={{ color: theme.palette.grey[700] }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleEditUser}
            disabled={!editUser.nome || !editUser.email || !editUser.login}
            sx={{ 
              px: 3,
              backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
              '&:hover': {
                boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`
              }
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Permissões */}
      <Dialog
        open={openRolesDialog}
        onClose={() => setOpenRolesDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VpnKeyIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              Gerenciar Permissões
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 3, p: 2, backgroundColor: theme.palette.grey[50], borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              Usuário: {currentUser?.nome}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
              Permissões Atuais:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              {userRoles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Nenhuma permissão atribuída
                </Typography>
              ) : (
                userRoles.map((roleObj) => (
                  <Chip 
                    key={roleObj.role}
                    label={roleObj.role}
                    onDelete={() => handleRemoveRole(roleObj.role)}
                    color="primary"
                    size="small"
                    sx={{ 
                      fontWeight: 500,
                      '&.MuiChip-colorPrimary': {
                        backgroundColor: `${theme.palette.primary.main}15`,
                        color: theme.palette.primary.dark,
                        '& .MuiChip-deleteIcon': {
                          color: theme.palette.primary.main,
                          '&:hover': {
                            color: theme.palette.primary.dark
                          }
                        }
                      }
                    }}
                  />
                ))
              )}
            </Box>
          </Box>
          
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 1, 
              alignItems: 'center',
              p: 2,
              border: `1px dashed ${theme.palette.primary.main}40`,
              borderRadius: 1,
              backgroundColor: `${theme.palette.primary.main}05`
            }}
          >
            <TextField
              fullWidth
              label="Nova Permissão"
              size="small"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2
                  }
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddRole}
              disabled={!newRole}
              startIcon={<AddCircleIcon />}
              sx={{ 
                whiteSpace: 'nowrap',
                backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                '&:hover': {
                  boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}60`
                }
              }}
            >
              Adicionar
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.grey[200]}` }}>
          <Button 
            variant="outlined"
            onClick={() => setOpenRolesDialog(false)}
            sx={{ 
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.error.light}40`,
          pb: 2,
          color: theme.palette.error.main
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DeleteIcon sx={{ color: theme.palette.error.main }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              Confirmar Exclusão
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, p: 3 }}>
          <Box sx={{ 
            backgroundColor: `${theme.palette.error.main}08`, 
            p: 2, 
            borderRadius: 1,
            borderLeft: `4px solid ${theme.palette.error.main}`
          }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Tem certeza que deseja excluir o usuário <strong>"{currentUser?.nome}"</strong>?
            </Typography>
            <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
              Esta ação não pode ser desfeita.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.grey[200]}` }}>
          <Button 
            onClick={() => setOpenDeleteDialog(false)}
            sx={{ color: theme.palette.grey[700] }}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteUser}
            sx={{ 
              px: 3,
              boxShadow: `0 4px 14px 0 ${theme.palette.error.main}40`,
              '&:hover': {
                boxShadow: `0 6px 20px 0 ${theme.palette.error.main}60`
              }
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default UsersPage; 